<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @hide="$emit('hide')"
      @page-break="$emit('page-break')"
    />

    <AnalyticsWrapper>
      <div :class="layoutClasses">
        <div
          v-if="label || description"
          class="formester-label-description-wrapper"
          :style="labelDescriptionWrapperStyles"
        >
          <label
            :class="labelClasses"
            :style="labelStyles"
            :for="item.label + item.id"
            v-html="label"
          />

          <SmartText
            :active="active"
            :editing="editing"
            :item="item"
            :style="descriptionStyles"
            :text="description"
          />
        </div>

        <div class="address">
          <div v-for="(subItem, idx) in item.children" :key="idx" :class="{}">
            <div v-if="!subItem.children" class="alone-field-wrapper">
              <div v-if="subItem.active" class="field-wrapper">
                <label
                  :style="subLabelStyle(subItem.label)"
                  :for="subItem.label"
                  :class="
                    subLabelClasses(
                      item.required &&
                        subItem.required &&
                        item.showRequriredMarkOnChildren,
                    )
                  "
                >
                  {{ subItem.label }}
                </label>

                <input
                  :id="subItem.label"
                  :value="getItemValue(subItem)"
                  :data-uuid="subItem.id"
                  :readonly="editing"
                  :class="inputClasses"
                  :name="`${item.label}_${subItem.label}`"
                  :required="item.required && subItem.required"
                  :style="[inputStyles, lockedFieldStyles(subItem)]"
                  :placeholder="subItem.placeholder"
                  :autocomplete="autocomplete ? 'off' : 'on'"
                  :disabled="fieldDisabled(subItem)"
                  @focus="handleFocus(subItem)"
                  @blur="handleBlur(subItem)"
                  @input="handleInput(subItem, $event.target.value)"
                />

                <!-- Address autocomplete suggestions -->
                <AddressSuggestions
                  v-if="
                    autocomplete && firstActiveAddressField?.id === subItem.id
                  "
                  :maps-a-p-i-key="mapsAPIKey"
                  :query="query"
                  @select="setAddress"
                />

                <LockIndicator :item="subItem" />
                <ErrorMessage :item="subItem" />
              </div>
            </div>

            <div v-else class="full-flex">
              <template v-for="innerItem in subItem.children">
                <div
                  v-if="innerItem.active"
                  :key="innerItem.label"
                  class="field-wrapper"
                >
                  <label
                    :style="subLabelStyle(innerItem.label)"
                    :for="innerItem.label"
                    :class="
                      subLabelClasses(
                        item.required &&
                          innerItem.required &&
                          item.showRequriredMarkOnChildren,
                      )
                    "
                  >
                    {{ innerItem.label }}
                  </label>

                  <input
                    :id="innerItem.label"
                    :value="getItemValue(innerItem)"
                    :data-uuid="innerItem.id"
                    :class="inputClasses"
                    :readonly="editing"
                    :name="`${item.label}_${innerItem.label}`"
                    :required="item.required && innerItem.required"
                    :style="[inputStyles, lockedFieldStyles(innerItem)]"
                    :placeholder="innerItem.placeholder"
                    :autocomplete="autocomplete ? 'off' : 'on'"
                    :disabled="fieldDisabled(innerItem)"
                    @input="handleInput(innerItem, $event.target.value)"
                    @focus="handleFocus(innerItem)"
                    @blur="handleBlur(innerItem)"
                  />

                  <AddressSuggestions
                    v-if="
                      autocomplete &&
                      firstActiveAddressField?.id === innerItem.id
                    "
                    :maps-a-p-i-key="mapsAPIKey"
                    :query="query"
                    @select="setAddress"
                  />

                  <LockIndicator :item="innerItem" />
                  <ErrorMessage :item="innerItem" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </AnalyticsWrapper>
  </div>
</template>

<script>
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import AddressSuggestions from "@/components/form_fields/shared/AddressSuggestions.vue";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import AnalyticsWrapper from "@/components/form_fields/shared/analytics-wrapper.vue";
import { set } from "lodash-es";
import { getAllFields, findItemByPropertyValue } from "@/helpers/utils.js";

export default {
  name: "AddressInput",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
    AnalyticsWrapper,
    AddressSuggestions,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      query: "",
    };
  },
  computed: {
    inputClasses() {
      return {
        "formester-input": true,
        "formester-input__address": true,
      };
    },
    firstActiveAddressField() {
      const fields = getAllFields(this.item);
      return (fields || []).find((field) => field.active);
    },
    autocomplete() {
      return this.item.autocomplete;
    },
    mapsAPIKey() {
      return this.item.mapsAPIKey;
    },
  },
  methods: {
    handleInput(item, value) {
      if (this.firstActiveAddressField?.id === item.id) {
        this.query = value;
      }
      this.setItemValue(item, value);
      this.handleFieldUpdate(item, value);
    },
    subLabelStyle(subLabel) {
      return {
        ...this.subLabelStyles,
        margin: !subLabel && "0px",
      };
    },
    setFieldValue(propertyValue, value = "") {
      const field = findItemByPropertyValue({
        items: this.item.children,
        property: "fixedname",
        value: propertyValue,
        nestedKey: "children",
      });

      if (field) {
        set(field, "value", value);
      }
    },
    setAddress({
      streetAddress,
      streetAddressLine2,
      postalCode,
      city,
      state,
      country,
    }) {
      this.setFieldValue("Street Address", streetAddress);
      this.setFieldValue("Street Address line 2", streetAddressLine2);
      this.setFieldValue("Postal/Zip Code", postalCode);
      this.setFieldValue("City", city);
      this.setFieldValue("State/Province", state);
      this.setFieldValue("Country", country);
    },
  },
};
</script>

<style scoped>
.full-flex {
  display: flex;
  width: 100%;
  gap: 19px;
}

.field-wrapper {
  width: 100%;
}

.field-wrapper:last-child {
  margin-right: 0;
}

.address {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

@media screen and (max-width: 540px) {
  .full-flex {
    flex-direction: column;
    gap: 8px;
  }
}

@container (max-width: 480px) {
  .full-flex {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
